import { useMemo } from "react";
import { useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { OceanCarrierDTO } from "../response.types";
import { PartialBy } from "shared/lib/typeCheck.lib";

import { sealinesService } from "../sealines.api";

type ResetSealinesQueryFunc = () => Promise<void>;

export type SeaLineOption = {
  name: string;
  scacCode: string;
  iconScac: string;
};

export type UseSealinesQueryReturn = {
  getSealines: () => void;
  resetSealinesQuery: ResetSealinesQueryFunc;
  sealines: OceanCarrierDTO[];
  seaLineOptions: SeaLineOption[];
  isSealinesSuccess: boolean;
  isSealinesError: boolean;
  isSealinesLoading: boolean;
};

type UseSealinesQueryParams = Pick<PartialBy<UseQueryOptions, "queryKey">, "queryKey"> & {
  requestOnMount?: boolean;
};

const SEALINES_CACHE_KEY = "get_sealines_query";

export const useSealinesQuery = ({
  queryKey = [SEALINES_CACHE_KEY],
  requestOnMount,
}: UseSealinesQueryParams = {}): UseSealinesQueryReturn => {
  const queryClient = useQueryClient();

  const { data, isError, isLoading, isSuccess } = useQuery({
    queryKey,
    queryFn: sealinesService.getSeaLines,
    select: (response) => response?.carriers ?? [],
    enabled: Boolean(requestOnMount),
  });

  const resetSealinesQuery: ResetSealinesQueryFunc = () => {
    return queryClient.resetQueries({ queryKey });
  };

  const getSealines = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  const seaLinesMappedToOptions = useMemo<SeaLineOption[]>(() => {
    if (!Array.isArray(data)) return [];

    return data.reduce<SeaLineOption[]>((acc, sealine) => {
      if (sealine.status?.is_active) {
        const iconScac = sealine.identifiers?.scac_codes?.[0] || sealine.code;
        acc.push({
          name: sealine.short_name || sealine.name,
          scacCode: sealine.code,
          iconScac,
        });
      }
      return acc;
    }, []);
  }, [data]);

  return {
    resetSealinesQuery,
    getSealines,
    sealines: data ?? [],
    seaLineOptions: seaLinesMappedToOptions,
    isSealinesSuccess: isSuccess,
    isSealinesError: isError,
    isSealinesLoading: isLoading,
  };
};
